import Button from "components/Button/Button";
import Dropdown from "components/Dropdown/Dropdown";
import Modal from "components/Modal/Modal";
import { CreateReportType, MarketplaceReports, ReportSchedule, ScheduledReportType } from "TypesAndInterfaces";
import "./CreateReport.scss";
import { createScheduledReportsMutattion, editScheduledReportsMutattion } from "pages/Reports/queries";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { useContext, useEffect, useState } from "react";
import { ReportsContext } from "pages/Reports/Reports.context";
import { format } from "date-fns";
import InputEmailField from "components/InputEmailField/InputEmailField";

interface IProps {
  initialReportState: CreateReportType;
  fetchReports: Function;
  isCreateReportModalOpened: boolean;
  setIsCreateReportModalOpened: Function;
  data: any[]
}

const CreateReport = ({
  initialReportState,
  fetchReports,
  isCreateReportModalOpened,
  setIsCreateReportModalOpened,
  data
}: IProps) => {
  //#region hooks
  const { selectedReport, setSelectedReport, marketplaceReports } = useContext(ReportsContext);
  const [createdReport, setCreatedReport] =
    useState<CreateReportType>(selectedReport.id !== undefined ? selectedReport : initialReportState);
  const [pageTitle, setPageTitle] = useState("");
  const [saveButtonText, setSaveButtonText] = useState("");
  const [disableButton, setDisableButton] = useState<boolean>(true);
  const [emailList, setEmailList] = useState<string>("")

  const marketplaceId = localStorage.getItem("marketplaceId");
  const scheduleOptions = Object.values(ReportSchedule).map((schedule) => ({
    value: schedule,
    label: schedule.replace(/([a-z])([A-Z])/g, '$1 $2'),
  }));

  const reportOptions = [
    { value: ScheduledReportType.GetInvoicesStatusReport, label: "Invoice Status Report" },
    { value: ScheduledReportType.GetShopKycStatusReport, label: "Shop KYC Status Report" },
    { value: ScheduledReportType.GetInvoicesLastMonthStatusReport, label: "Invoice Last Month Status Report" }
  ]

  //#endregion

  //#region API CALLS
  const [createReport, { loading }] = useMutation(createScheduledReportsMutattion, {
    onCompleted: () => {
      toast.info(`Report created.`, {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      fetchReports();
      setIsCreateReportModalOpened(false);
      resetCreateUserData();
    },
    onError: () => {
      toast.error(`error! submitted data:, ${createdReport.sqlFunctionName},
            ${createdReport.marketplaceId},
            ${createdReport.hour},
            ${createdReport.schedule},
             ${createdReport.emails},
          `);
    },
  });

  const [updateReport] = useMutation(editScheduledReportsMutattion, {
    onCompleted: () => {
      fetchReports();
      setIsCreateReportModalOpened(false);
      resetCreateUserData();
      window.location.reload();
      toast.info(`Report updated.`, {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

    },
    onError: () => {
      toast.error(`error! submitted data:, ${createdReport.sqlFunctionName},
        ${createdReport.marketplaceId},
        ${createdReport.hour},
        ${createdReport.schedule},
         ${createdReport.emails},
      `);
    },
  });

  //#endregion

  useEffect(() => {
    setCreatedReport(selectedReport);
    if (selectedReport.sqlFunctionName &&
      selectedReport.marketplaceId &&
      selectedReport.schedule && selectedReport.emails.length > 0) {
      setDisableButton(false)
    }
  }, [selectedReport])


  const resetCreateUserData = () => {
    setCreatedReport(initialReportState);
    setSelectedReport(initialReportState);
    setDisableButton(true);
    setEmailList("");
  };

  const handleIsCreateReportModalOpened = (isOpened: boolean) => {
    resetCreateUserData();
    setIsCreateReportModalOpened(isOpened);
    if (!isOpened) {
      resetCreateUserData();
    }
  };
  const findMarketplaceName = () => {
    const marketplaceName = data.find((report) => {
      return report.marketplaceId = marketplaceId
    })
    const name = marketplaceName?.name.split("-")[0]
    return name ?? "";
  }

  const handleCreateUser = async () => {
    if (selectedReport.id) {
      await updateReport({
        variables: {
          data: {
            id: selectedReport.id,
            sqlFunctionName: createdReport.sqlFunctionName,
            marketplaceId: createdReport.marketplaceId,
            hour: createdReport.hour ?? 0,
            schedule: createdReport.schedule,
            emails: createdReport.emails ?? "",
          },
        },
      });
      fetchReports();
      resetCreateUserData();
    } else {
      await createReport({
        variables: {
          data: {
            id: Math.floor(Math.random()),
            sqlFunctionName: createdReport.sqlFunctionName,
            marketplaceId: createdReport.marketplaceId,
            hour: createdReport.hour ?? 0,
            schedule: createdReport.schedule,
            emails: createdReport.emails ?? "",
          },
        },
      });
      fetchReports();
      resetCreateUserData();
    }
  };

  useEffect(() => {
    if (selectedReport.id) {
      setPageTitle("Edit Report");
      setSaveButtonText("Edit");
    } else {
      setPageTitle("Create a New Report");
      setSaveButtonText("Create");
    }
  }, [selectedReport]);

  const UTCValues: { value: number, label: string }[] = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: i.toString()
  }));

  type ScheduleOption = {
    value: string;
    label: string;
  };

  const marketplaceOptiosn: ScheduleOption[] = marketplaceReports.map((obj: MarketplaceReports) => {
    const firstName = obj.name.split(" - ")[0];
    return {
      label: `${obj.marketplaceId} - ${firstName}`,
      value: obj.marketplaceId
    };
  });

  const validateEmails = (input: string) => {
    const regex = /^[\w-\\.\\+]+@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const emails = input.replace(/"/g, '').split(',');
    const filteredArray = emails.filter(element => element !== '');
    setDisableButton(!filteredArray.every(email => regex.test(email.trim())));
  }

  const getCESTTime = () => {
    const date = new Date();
    createdReport.hour && date.setHours(createdReport.hour + 2);
    const hour = format(date, "hh a")
    const message = createdReport.hour + " UTC = " + hour + " CE(S)T"
    return message
  }

  useEffect(() => {
    setEmailList(createdReport?.emails.replace(/"/g, ''))
  }, [createdReport]);

  useEffect(() => {
    setCreatedReport((prev: CreateReportType) => ({
      ...prev,
      emails: emailList,
    }));
    if (emailList.length === 0) {
      setDisableButton(true)
    }
  }, [emailList])

  return (
    <Modal
      isOpened={isCreateReportModalOpened}
      className="user-form"
      title={pageTitle}
      setIsOpenedCallback={handleIsCreateReportModalOpened}
    >
      <form className="form">
        <Dropdown
          options={reportOptions}
          placeholder={"Report"}
          label={"Report"}
          value={reportOptions.find((option) => {
            return option.value === createdReport.sqlFunctionName;
          })}
          onChange={(e: any) => {
            setCreatedReport((prev: CreateReportType) => ({
              ...prev,
              sqlFunctionName: e.value,
            }))
          }}
        />
        {marketplaceId ?
          <Dropdown
            options={[{ value: marketplaceId, label: (marketplaceId + " - " + findMarketplaceName()) }]}
            placeholder={"Marketplace"}
            label={"Marketplace"}
            value={marketplaceId ? { value: marketplaceId, label: (marketplaceId + " - " + findMarketplaceName()) } : "Marketplace"}
            onChange={(e: any) => {
              const name = e.label.split("-")[1];
              setCreatedReport((prev: CreateReportType) => ({
                ...prev,
                marketplaceId: +e.value,
                name: name
              }))
            }}
          /> : <Dropdown
            options={marketplaceOptiosn}
            placeholder={"Marketplace"}
            label={"Marketplace"}
            value={createdReport.marketplaceId ? { value: createdReport.marketplaceId?.toString(), label: (createdReport.marketplaceId?.toString() + " - " + createdReport.name.split("-")[0]) } : "Marketplace"}
            onChange={(e: any) => {
              const name = e.label.split("-")[1];
              setCreatedReport((prev: CreateReportType) => ({
                ...prev,
                marketplaceId: +e.value,
                name: name
              }))
            }}
          />}
        <Dropdown
          options={scheduleOptions}
          placeholder={"Report Frequency"}
          label={"Report Frequency"}
          value={createdReport.schedule ? { value: createdReport.schedule, label: createdReport.schedule } : "Report Frequency"}
          onChange={(e: any) => setCreatedReport((prev: CreateReportType) => ({
            ...prev,
            schedule: e.value,
          }))}
        />
        {createdReport.schedule && createdReport.schedule !== ReportSchedule.OnDemand && (
          <Dropdown
            options={UTCValues}
            placeholder={"UTC Hour"}
            label={"UTC Hour"}
            value={createdReport.hour ? { value: createdReport.hour?.toString(), label: createdReport.hour } : "UTC Hour"}
            infoMessage={getCESTTime()}
            onChange={(e: any) => {
              setCreatedReport((prev: CreateReportType) => ({
                ...prev,
                hour: +e.value,
              }))
            }}
            infoIcon={true}
          />
        )}
        {createdReport.schedule && createdReport.schedule !== ReportSchedule.OnDemand && (
          <InputEmailField
            label={"Email List"}
            placeholder="Enter emails separated by comma or press enter"
            type="email"
            onChange={(e) => {
              validateEmails(e.target.value);
            }}
            defaultValue={createdReport?.emails.replace(/"/g, '')}
            setEmails={setEmailList}
          />
        )}

      </form>
      <div className="modal-buttons-container">
        <Button
          className="button--bordered button--error"
          type="button"
          content="Cancel"
          onClick={() => handleIsCreateReportModalOpened(false)}
        ></Button>
        {loading ? (
          <Button
            className="button--primary"
            type="button"
            icon="sync"
            content={"Loading..."}
            disabled={true}
          ></Button>
        ) : (
          <Button
            className="button--primary"
            type="button"
            content={saveButtonText}
            onClick={handleCreateUser}
            disabled={
              !createdReport.sqlFunctionName ||
              !createdReport.marketplaceId ||
              !createdReport.schedule ||
              (createdReport.schedule !== ReportSchedule.OnDemand &&
                (!createdReport.hour || disableButton))
            }
          ></Button>)}
      </div>
    </Modal>
  );
};

export default CreateReport;
